import Phaser from 'phaser';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { LeaderboardClient, type LeaderboardScore, type Period } from '../systems/LeaderboardClient';
import { PlayerProfile } from '../systems/PlayerProfile';

// === SCÈNE LEADERBOARD EN LIGNE ===
// Top 50 scores, filtres acte + période, mise en surbrillance du pseudo joueur.
// Lecture seule : la soumission est gérée par RunEndScene.

const HEADER_H = 95;
const CLASS_EMOJI: Record<string, string> = {
  warrior: '⚔️',
  ranger: '🏹',
  medic: '💊',
};

interface SceneData {
  returnTo?: string;
}

export class LeaderboardScene extends Phaser.Scene {
  private panel: ScrollablePanel | null = null;
  private returnTo = 'BootScene';
  private acteFilter: number | null = null;  // null = tous actes
  private periodFilter: Period = 'all';
  private currentScores: LeaderboardScore[] = [];
  private loading = true;
  private fetchError: string | null = null;

  constructor() {
    super({ key: 'LeaderboardScene' });
  }

  init(data: SceneData): void {
    this.returnTo = data?.returnTo ?? 'BootScene';
    this.acteFilter = null;
    this.periodFilter = 'all';
    this.currentScores = [];
    this.loading = true;
    this.fetchError = null;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
    // Premier chargement
    this.refreshScores();
  }

  shutdown(): void {
    this.panel?.destroy();
    this.panel = null;
  }

  // === RENDU ===

  private drawUI(): void {
    this.panel?.destroy();
    this.panel = null;
    this.children.removeAll(true);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    this.add.text(GAME_CX, 18, '🏆 CLASSEMENT EN LIGNE', {
      fontSize: '18px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Bouton retour (ancré à gauche)
    const backBtn = this.add.text(8, 12, '◀ Retour', {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    }).setOrigin(0, 0).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, this.returnTo));
    backBtn.on('pointerover', () => backBtn.setColor('#ffffff'));
    backBtn.on('pointerout', () => backBtn.setColor('#bdc3c7'));

    // Bouton refresh (à droite)
    const refreshBtn = this.add.text(GAME_W - 8, 12, '🔄', {
      fontSize: '14px', color: '#3498db', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a3a', padding: { x: 8, y: 4 },
    }).setOrigin(1, 0).setDepth(11);
    refreshBtn.setInteractive({ useHandCursor: true });
    refreshBtn.on('pointerdown', () => this.refreshScores());
    refreshBtn.on('pointerover', () => refreshBtn.setColor('#ffffff'));
    refreshBtn.on('pointerout', () => refreshBtn.setColor('#3498db'));

    // === FILTRES ===
    // Ligne 1 : acte
    this.drawActeFilter(40);
    // Ligne 2 : période
    this.drawPeriodFilter(66);

    // === PANEL SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);
    this.drawScoresList();
  }

  private drawActeFilter(y: number): void {
    const labels: Array<[string, number | null]> = [
      ['Tous', null],
      ['Acte 1', 1],
      ['Acte 2', 2],
      ['Acte 3', 3],
    ];
    this.drawFilterRow(y, labels, this.acteFilter, (val) => {
      this.acteFilter = val;
      this.refreshScores();
    });
  }

  private drawPeriodFilter(y: number): void {
    const labels: Array<[string, Period]> = [
      ['Total', 'all'],
      ['Mois', 'monthly'],
      ['Semaine', 'weekly'],
      ['Jour', 'daily'],
    ];
    this.drawFilterRow(y, labels, this.periodFilter, (val) => {
      this.periodFilter = val;
      this.refreshScores();
    });
  }

  private drawFilterRow<T>(y: number, labels: Array<[string, T]>, current: T, onSelect: (val: T) => void): void {
    // Centrer la rangée dans la largeur disponible
    const gap = 6;
    const padX = 7;
    const fontSize = 10;
    // Mesure approximative (Phaser n'expose pas measure sans créer l'objet)
    const widths = labels.map(([label]) => label.length * 6.2 + padX * 2);
    const totalW = widths.reduce((a, b) => a + b, 0) + gap * (labels.length - 1);
    let x = GAME_CX - totalW / 2;

    for (let i = 0; i < labels.length; i++) {
      const [label, val] = labels[i];
      const isActive = current === val;
      const btn = this.add.text(x, y, label, {
        fontSize: `${fontSize}px`,
        color: isActive ? '#f1c40f' : '#7f8c8d',
        fontFamily: FONT_BODY,
        fontStyle: isActive ? 'bold' : 'normal',
        backgroundColor: isActive ? '#3a2a1a' : '#1a1a2e',
        padding: { x: padX, y: 3 },
      }).setOrigin(0, 0).setDepth(11);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => onSelect(val));
      btn.on('pointerover', () => { if (!isActive) btn.setColor('#ecf0f1'); });
      btn.on('pointerout', () => { if (!isActive) btn.setColor('#7f8c8d'); });
      x += widths[i] + gap;
    }
  }

  private drawScoresList(): void {
    if (!this.panel) return;

    const myPseudo = PlayerProfile.getPseudo();
    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 6);
    let y = 8;

    if (!LeaderboardClient.isEnabled()) {
      const msg = this.add.text(GAME_CX, y + 40,
        '⚠️ Classement en ligne désactivé.\n\nL\'URL de l\'API n\'est pas configurée\ndans ce build (VITE_LEADERBOARD_API_URL).',
        { fontSize: '12px', color: '#e67e22', fontFamily: FONT_BODY, align: 'center' },
      ).setOrigin(0.5, 0);
      this.panel.add(msg);
      this.panel.setContentHeight(y + 140);
      return;
    }

    if (this.loading) {
      const msg = this.add.text(GAME_CX, y + 40, '⏳ Chargement...', {
        fontSize: '13px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5, 0);
      this.panel.add(msg);
      this.panel.setContentHeight(y + 80);
      return;
    }

    if (this.fetchError) {
      const msg = this.add.text(GAME_CX, y + 40,
        `❌ Impossible de charger le classement.\n${this.fetchError}\n\nTouche 🔄 pour réessayer.`,
        { fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY, align: 'center' },
      ).setOrigin(0.5, 0);
      this.panel.add(msg);
      this.panel.setContentHeight(y + 140);
      return;
    }

    if (this.currentScores.length === 0) {
      const msg = this.add.text(GAME_CX, y + 40, 'Aucun score pour ce filtre.\nSois le premier à t\'inscrire !', {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY, align: 'center',
      }).setOrigin(0.5, 0);
      this.panel.add(msg);
      this.panel.setContentHeight(y + 120);
      return;
    }

    // En-tête colonnes
    this.panel.add(this.add.text(lx, y, '#', { fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY }));
    this.panel.add(this.add.text(lx + 30, y, 'Pseudo', { fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY }));
    this.panel.add(this.add.text(lx + 165, y, 'Cl.', { fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY }));
    this.panel.add(this.add.text(lx + 195, y, 'Ét.', { fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY }));
    this.panel.add(this.add.text(lx + 225, y, 'Score', { fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY }));
    y += 16;

    // Ligne séparatrice
    const sep = this.add.rectangle(GAME_CX, y, rowW, 1, 0x2c3e50);
    this.panel.add(sep);
    y += 6;

    // Lignes du leaderboard
    let rank = 1;
    for (const s of this.currentScores) {
      const isMe = myPseudo !== null && s.pseudo === myPseudo;
      const bgColor = isMe ? 0x2a3a1a : (rank % 2 === 0 ? 0x12182a : 0x0a0e1c);

      // Fond de ligne
      const bg = this.add.rectangle(GAME_CX, y + 11, rowW, 22, bgColor);
      if (isMe) bg.setStrokeStyle(1, 0x2ecc71);
      this.panel.add(bg);

      // Rang : couleurs spéciales pour le podium
      let rankColor = '#7f8c8d';
      if (rank === 1) rankColor = '#f1c40f';
      else if (rank === 2) rankColor = '#bdc3c7';
      else if (rank === 3) rankColor = '#cd7f32';
      if (isMe) rankColor = '#2ecc71';

      this.panel.add(this.add.text(lx, y + 4, String(rank), {
        fontSize: '11px', color: rankColor, fontFamily: FONT_BODY, fontStyle: rank <= 3 ? 'bold' : 'normal',
      }));

      // Pseudo (tronqué si trop long)
      const pseudoDisplay = s.pseudo.length > 16 ? s.pseudo.slice(0, 15) + '…' : s.pseudo;
      this.panel.add(this.add.text(lx + 30, y + 4, pseudoDisplay, {
        fontSize: '11px',
        color: isMe ? '#2ecc71' : '#ecf0f1',
        fontFamily: FONT_BODY,
        fontStyle: isMe ? 'bold' : 'normal',
      }));

      // Classe
      this.panel.add(this.add.text(lx + 165, y + 4, CLASS_EMOJI[s.classe] ?? '?', {
        fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY,
      }));

      // Étage
      this.panel.add(this.add.text(lx + 195, y + 4, `${s.etage}${s.victory ? '🏆' : ''}`, {
        fontSize: '11px', color: '#3498db', fontFamily: FONT_BODY,
      }));

      // Score
      this.panel.add(this.add.text(lx + 225, y + 4, String(s.score), {
        fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      }));

      y += 24;
      rank++;
    }

    // Bandeau de pied : ton meilleur rang si le joueur n'apparaît pas dans la page
    const meInPage = myPseudo && this.currentScores.some((s) => s.pseudo === myPseudo);
    if (myPseudo && !meInPage) {
      y += 12;
      const footer = this.add.text(GAME_CX, y, `(${myPseudo} : pas encore dans le top ${this.currentScores.length})`, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'italic',
      }).setOrigin(0.5, 0);
      this.panel.add(footer);
      y += 20;
    }

    this.panel.setContentHeight(y + 20);
  }

  // === RÉCUPÉRATION DONNÉES ===

  private async refreshScores(): Promise<void> {
    this.loading = true;
    this.fetchError = null;
    this.currentScores = [];
    this.drawUI();

    const result = await LeaderboardClient.list({
      limit: 50,
      acte: this.acteFilter ?? undefined,
      period: this.periodFilter,
    });

    this.loading = false;
    if (result.ok) {
      this.currentScores = result.scores;
    } else {
      this.fetchError = result.error ?? 'Erreur inconnue';
    }
    // Si la scène a été quittée entre-temps, ne pas redessiner
    if (this.scene.isActive('LeaderboardScene')) {
      this.drawUI();
    }
  }
}
